import { URLS, createJurisdictionHeaders } from '@/data';
import { authUserKeys } from '@/hooks/use-auth-user-config';
import { logger } from '@/utils/logging';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { getSocureDeviceSessionToken } from '../utils/socure-device-session';

/**
 * Response of POST /auth/user/polymarket/kyc.
 *
 * The endpoint wraps the payload under `data`. `success` only indicates the submission call
 * itself succeeded — the KYC OUTCOME is in `decision`:
 *  - success: true,  decision: 'ACCEPT', docv: null      -> approved
 *  - success: true,  decision: 'REVIEW', docv: { ... }   -> Socure DocV document capture required
 *  - success: true,  decision: 'REJECT', docv: null      -> rejected (NOT approved — lock out)
 *  - success: true,  docv: null, decision absent         -> already-approved early return
 *  - success: false (HTTP 422)                           -> blocked / validation / internal failure
 *
 */
const DocvSchema = z
    .object({
        url: z.string().optional(),
        sdkKey: z.string().optional(),
        qrCode: z.string().optional(),
    })
    .passthrough();

// Kept as a loose string (ACCEPT | REJECT | REVIEW | '' | absent) so an unexpected value can't
// fail the parse; the gate compares against the known outcomes explicitly.
export const PredictionKycResponseSchema = z.object({
    success: z.boolean(),
    message: z.string().optional(),
    http_status: z.number().nullable().optional(),
    decision: z.string().nullable().optional(),
    docv: DocvSchema.nullable().optional(),
});

export type PredictionKycResponse = z.infer<typeof PredictionKycResponseSchema>;

const submitPolymarketKyc = async (): Promise<PredictionKycResponse> => {
    const sessionToken = await getSocureDeviceSessionToken();
    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/user/polymarket/kyc`, {
        method: 'POST',
        headers: {
            ...createJurisdictionHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(sessionToken ? { session_token: sessionToken } : {}),
    });

    const json = await response.json();
    logger.debug('submitPolymarketKyc', json);
    return PredictionKycResponseSchema.parse(json?.data);
};

export const usePredictionKycSubmit = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['prediction-kyc-submit'],
        mutationFn: submitPolymarketKyc,
        onSuccess: data => {
            if (data.success && !data.docv && data.decision !== 'REJECT') {
                queryClient.invalidateQueries({ queryKey: authUserKeys.config() });
            }
        },
    });
};
